import React, { useEffect, useState } from 'react';
import * as _ from 'lodash';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import DialogList from 'src/dialogs';
import useFetch from 'use-http';
import { ODataOptions } from 'src/util/ODataBuilder';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import { ITableConfig } from 'src/components/common/table';
import TimeAndExpense from 'src/models/AP/TimeAndExpense';
import InvoiceOfficeBreakdown from '../models/InvoiceOfficeBreakdown';
import BilledExpense from 'src/models/projectSettings/BilledExpense';

interface IInvoiceBreakdownDialogProps {
    dialogName: string;
    payPeriod: string;
    invoicedOffices?: Array<InvoiceOfficeBreakdown>;
    handleReport?: (category: string, subcategory: string) => void;
    hideDialog: () => void;
    showDialog: (dialogName: DialogList) => void;
    showAddSubcodeDialog?: () => void;
    setAddingAltSubcode?: (addingAltSubcode: boolean) => void;
    setFindingAltSubcode?: (findingAltSubcode: boolean) => void;
    setShowBillingSettings?: (showBillingSettings: boolean) => void;
    timeAndExpenses?: Array<TimeAndExpense>;
    companyName?: string;
}

const InvoiceBreakdownDialog = (props: IInvoiceBreakdownDialogProps) => {
    const { register, errors, getValues } = useForm();
    const { get, response } = useFetch();
    const [billedExpensesSearchOptions, setBilledExpensesSearchOptions] = useState<ODataOptions>(undefined);
    const [billedExpensesByOffice, setBilledExpensesByOffice] = useState<Array<InvoiceOfficeBreakdown>>(new Array<InvoiceOfficeBreakdown>());
    const [loadingBilledExpensesByOffice, setLoadingBilledExpensesByOffice] = useState<boolean>(true);
    const [billedExpensesSearchTerm, setBilledExpensesSearchTerm] = useState<string>('');
    const [loadingOfficeInvoiceSummary, setLoadingOfficeInvoiceSummary] = useState<boolean>(true);
    const [officeInvoices, setOfficeInvoices] = useState<Array<InvoiceOfficeBreakdown>>([]);
    const [selectedInvoiceOffice, setSelectedInvoiceOffice] = useState<number>(undefined);
    const [selectedPayPeriod, setSelectedPayPeriod] = useState<string>(undefined);
    const [selectedOffice, setSelectedOffice] = useState<string>(undefined);
    const { register: registerOfficeInoices, reset: resetOfficeInvoices, errors: officeInvoiceErrors } = useForm();

    useEffect(() => {
        onCreate();
    }, []);

    useEffect(() => {
        if (!!props?.payPeriod) {
            setSelectedPayPeriod(props?.payPeriod);
        }
    }, [props?.payPeriod]);

    useEffect(() => {
        if (!!officeInvoices) {
            setLoadingOfficeInvoiceSummary(false);
        }
    }, [officeInvoices]);

    useEffect(() => {
        if (!!billedExpensesByOffice) {
            setLoadingBilledExpensesByOffice(false);
        }
    }, [billedExpensesByOffice]);

    useEffect(() => {
        if (!!billedExpensesSearchOptions) {
            setBilledExpensesSearchTerm('');
            getBilledExpensesByOffice();
        }
    }, [billedExpensesSearchOptions]);

    useEffect(() => {
        if ((!!billedExpensesSearchTerm || billedExpensesSearchTerm === '') && !!billedExpensesSearchOptions) {
            resetBilledExpensesSearchOptions();
        }
    }, [billedExpensesSearchTerm]);

    const onCreate = () => {
        initializeStaffingLandmanSearchOptions();
        setLoadingOfficeInvoiceSummary(false);
        getInvoiceOfficeBreakdown();
        resetOfficeInvoices();
        console.log(selectedInvoiceOffice);
    };

    const getInvoiceOfficeBreakdown = async () => {
        const payPeriod = selectedPayPeriod;
        if (!!payPeriod && !!selectedOffice) {
            let result = await get(`/v1.0/AccountsPayable/expenses/payperiod/${payPeriod}`);

            if (response.ok) {
                setOfficeInvoices(result?.items);
            } else {
                console.log('Unable to get client landmen Data!');
            }
        }
    };

    const getBilledExpensesByOffice = async () => {
        const payPeriod = selectedPayPeriod;
        if (!!billedExpensesSearchOptions && !!selectedOffice && !!payPeriod) {
            let queryString = !!billedExpensesSearchOptions?.toString() ? billedExpensesSearchOptions?.toString() : '';
            let result = await get(`/v1.0/AccountsPayable/expenses/payperiod/${payPeriod}/office/{selectedOffice}${queryString}`);

            if (response.ok) {
                setBilledExpensesByOffice(result?.items);
            } else {
                console.log('Unable to get client landmen Data!');
            }
        }
    };

    const initializeStaffingLandmanSearchOptions = () => {
        const tableConfig: ITableConfig = {
            rowCount: 250,
            idField: 'CL_NO',
            pageNumber: 1,
            columns: []
        };
        const options = ODataOptions.from(tableConfig);
        setBilledExpensesSearchOptions(options);
    };

    const resetBilledExpensesSearchOptions = () => {
        let newBilledExpensesSearchOptions = _.cloneDeep(billedExpensesSearchOptions);
        newBilledExpensesSearchOptions.setIsNextpage(true);
        newBilledExpensesSearchOptions.clearFilters();
        newBilledExpensesSearchOptions.addPaging(1, 100);
        if (!selectedOffice) {
            return;
        }

        setBilledExpensesSearchOptions(newBilledExpensesSearchOptions);
    };

    const onSubmit = () => {
        console.log('OK Clicked');
        let data = getValues();

        props.handleReport(getValues('category'), getValues('subcategory'));
        props.hideDialog();

        console.log(data);
    };

    const displayTitle = () => {
        const title = 'Offices';

        return props?.companyName ? props?.companyName + ' ' + title : title;
    };

    const onCancel = () => {
        props.hideDialog();        
    };

    const displayHeading2 = () => {
        const title = 'Invoice Totals by Client';

        return title;
    };

    const displayOfficeInvoiceSummary = () => {
        return (
            <table className={'project-billing'}>
                <thead className={'project-billing'}>
                    <th>Office</th>
                    <th>LM Days</th>
                    <th>LM Services</th>
                    <th>BU Days</th>
                    <th>BU Amount</th>
                    <th>BTS Days</th>
                    <th>BTS Amount</th>
                    <th>Expenses</th>
                    <th>Days</th>
                    <th>Amount</th>
                </thead>
                <tbody className={'project-billing'}>
                {!loadingBilledExpensesByOffice && !!billedExpensesByOffice && billedExpensesByOffice?.length > 0 && billedExpensesByOffice?.map(
                    (ob: InvoiceOfficeBreakdown, index: number) => {
                    return (
                        <tr
                            key={index}
                            tabIndex={0}
                            id={'leftPanelRow' + index}
                            className="table-item-hover"
                            style={{ textAlign: 'center' }}
                            onClick={() => setSelectedInvoiceOffice(index)}
                        >
                            <td style={{ width: '5%' }}>
                                <RHFInputBox
                                    name={`officeTimeAndExpensesEntries[${index}].office`}
                                    readOnly={true}
                                    type="text"
                                    defaultVal={ob?.office}
                                    register={registerOfficeInoices}
                                    errors={officeInvoiceErrors}
                                    style={{ width: '100%' } }
                                    bodyStyle={{ width: '100%' }}
                                />
                            </td>
                            <td style={{ width: '4%' }}>
                                <RHFInputBox
                                    name={`officeTimeAndExpensesEntries[${index}].type`}
                                    readOnly={true}
                                    type="text"
                                    defaultVal={ob?.lmDays?.toString()}
                                    register={registerOfficeInoices}
                                    errors={officeInvoiceErrors}
                                    style={{ width: '100%' } }
                                    bodyStyle={{ width: '100%' }}
                                />
                            </td>
                            <td style={{ width: '6%' }}>
                                <RHFInputBox
                                    name={`officeTimeAndExpensesEntries[${index}].landmenCount`}
                                    readOnly={true}
                                    type="number"
                                    defaultVal={ob.lmServices?.toString()}
                                    register={registerOfficeInoices}
                                    errors={officeInvoiceErrors}
                                    style={{ width: '100%' } }
                                    bodyStyle={{ width: '100%' }}
                                />
                            </td>
                            <td style={{ width: '6%' }}>
                                <RHFInputBox
                                    name={`officeTimeAndExpensesEntries[${index}].entered`}
                                    readOnly={true}
                                    type="number"
                                    defaultVal={ob?.buDays?.toString()}
                                    register={registerOfficeInoices}
                                    errors={officeInvoiceErrors}
                                    style={{ width: '100%' } }
                                    bodyStyle={{ width: '100%' }}
                                />
                            </td>
                            <td style={{ width: '6%' }}>
                                <RHFInputBox
                                    name={`officeTimeAndExpensesEntries[${index}].invoiced`}
                                    readOnly={true}
                                    type="number"
                                    defaultVal={ob?.buAmount?.toString()}
                                    register={registerOfficeInoices}
                                    errors={officeInvoiceErrors}
                                    style={{ width: '100%' } }
                                    bodyStyle={{ width: '100%' }}
                                />
                            </td>
                            <td style={{ width: '6%' }}>
                                <RHFInputBox
                                    name={`officeTimeAndExpensesEntries[${index}].proof`}
                                    readOnly={true}
                                    type="number"
                                    defaultVal={ob?.btsDays?.toString()}
                                    register={registerOfficeInoices}
                                    errors={officeInvoiceErrors}
                                    style={{ width: '100%' } }
                                    bodyStyle={{ width: '100%' }}
                                />
                            </td>
                            <td style={{ width: '6%' }}>
                                <RHFInputBox
                                    name={`officeTimeAndExpensesEntries[${index}].paid`}
                                    readOnly={true}
                                    type="number"
                                    defaultVal={ob.btsAmount?.toString()}
                                    register={registerOfficeInoices}
                                    errors={officeInvoiceErrors}
                                    style={{ width: '100%' } }
                                    bodyStyle={{ width: '100%' }}
                                />
                            </td>
                            <td style={{ width: '6%' }}>
                                <RHFInputBox
                                    name={`officeTimeAndExpensesEntries[${index}].paid`}
                                    readOnly={true}
                                    type="number"
                                    defaultVal={ob.expenses?.toString()}
                                    register={registerOfficeInoices}
                                    errors={officeInvoiceErrors}
                                    style={{ width: '100%' } }
                                    bodyStyle={{ width: '100%' }}
                                />
                            </td>
                            <td style={{ width: '6%' }}>
                                <RHFInputBox
                                    name={`officeTimeAndExpensesEntries[${index}].paid`}
                                    readOnly={true}
                                    type="number"
                                    defaultVal={ob.days?.toString()}
                                    register={registerOfficeInoices}
                                    errors={officeInvoiceErrors}
                                    style={{ width: '100%' } }
                                    bodyStyle={{ width: '100%' }}
                                />
                            </td>
                            <td style={{ width: '6%' }}>
                                <RHFInputBox
                                    name={`officeTimeAndExpensesEntries[${index}].paid`}
                                    readOnly={true}
                                    type="number"
                                    defaultVal={ob.amount?.toString()}
                                    register={registerOfficeInoices}
                                    errors={officeInvoiceErrors}
                                    style={{ width: '100%' } }
                                    bodyStyle={{ width: '100%' }}
                                />
                            </td>
                        </tr>
                    );
                })}
                </tbody>
            </table>
        );
    };

    const displayInvoicesByOffice = () => {
        return <div>
            <table>
                <thead>                    
                    <tr style={{ textAlign: 'left' }}>
                        <th style={{ width: '2.5%' }}>Client #</th>
                        <th style={{ width: '10%' }}/>
                        <th style={{ width: '5%' }}>LM Days</th>
                        <th style={{ width: '10%' }}>LM Services</th>
                        <th style={{ width: '3%' }}>BU Days</th>
                        <th style={{ width: '3%' }}>BU Amount</th>
                        <th style={{ width: '3%' }}>Days (BTS)</th>
                        <th style={{ width: '3%' }}>Amount (BTS)</th>
                        <th style={{ width: '3%' }}>Expenses</th>
                        <th style={{ width: '3%' }}>Days</th>
                        <th style={{ width: '3%' }}>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {!loadingOfficeInvoiceSummary && !!officeInvoices && officeInvoices.length > 0 && officeInvoices?.map(
                        (be: BilledExpense, index: number) => {
                        return (
                                <tr
                                    key={be?.id + index}
                                    className="table-item-hover"
                                    style={{ textAlign: 'center' }}
                                    onClick={() => setSelectedOffice(officeInvoices[index]?.office)}
                                >
                                    <td style={{ width: '2.5%' }}>
                                        <RHFInputBox
                                            name={`clientNumber`}
                                            type="text"
                                            defaultVal={be?.clientNumber?.toString()}
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '100%' }}
                                        />
                                    </td>
                                    <td style={{ width: '10%' }}>
                                        <RHFInputBox
                                            name={`clientName`}
                                            type="text"
                                            defaultVal={be?.clientName}
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%', background: '#d5f7f5' }}
                                            bodyStyle={{ width: '100%' }}
                                        />
                                    </td>
                                    <td style={{ width: '5%' }}>
                                        <RHFInputBox
                                            name={`lmDays`}
                                            type="text"
                                            defaultVal={!!be?.landmanDays ? be?.landmanDays?.toString() : ''}
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '100%' }}
                                        />
                                    </td>
                                    <td style={{ width: '10%' }}>
                                        <RHFInputBox
                                            name={`lmServices`}
                                            type="text"
                                            defaultVal={!!be?.landmanServices ? be?.landmanServices?.toString() : ''}
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '100%' }}
                                        />
                                    </td>
                                    <td style={{  width: '3%' }}>
                                        <RHFInputBox
                                            name={`buDays`}
                                            defaultVal={be?.backupDays?.toString()}
                                            style={{ width: '40px'}}
                                            register={register}
                                            errors={errors}
                                        />
                                    </td>
                                    <td style={{ width: '3%' }}>
                                        <RHFInputBox
                                            name={`buAmount`}
                                            defaultVal={!!be?.backupAmount ? be?.backupAmount.toString() : ''}
                                            style={{ width: '40px'}}
                                            register={register}
                                            errors={errors}
                                        />
                                    </td>
                                    <td style={{ width: '3%' }}>
                                        <RHFInputBox
                                            name={`btsDays`}
                                            defaultVal={!!be?.btsDays ? be?.btsDays.toString() : ''}
                                            style={{ width: '40px'}}
                                            register={register}
                                            errors={errors}
                                        />
                                    </td>
                                    <td style={{ width: '3%' }}>
                                        <RHFInputBox
                                            name={`btsAmount`}
                                            defaultVal={!!be?.btsAmount ? be?.btsAmount.toString() : ''}
                                            style={{ width: '40px'}}
                                            register={register}
                                            errors={errors}
                                        />
                                    </td>
                                    <td style={{ width: '3%' }}>
                                        <RHFInputBox
                                            name={`expenses`}
                                            defaultVal={!!be?.expenses ? be?.expenses.toString() : ''}
                                            style={{ width: '40px'}}
                                            register={register}
                                            errors={errors}
                                        />
                                    </td>
                                    <td style={{ width: '3%' }}>
                                        <RHFInputBox
                                            name={`days`}
                                            defaultVal={!!be?.days ? be?.days.toString() : ''}
                                            style={{ width: '40px'}}
                                            register={register}
                                            errors={errors}
                                        />
                                    </td>
                                    <td style={{ width: '3%' }}>
                                        <RHFInputBox
                                            name={`amount`}
                                            defaultVal={!!be?.amount ? be?.amount.toString() : ''}
                                            style={{ width: '40px'}}
                                            register={register}
                                            errors={errors}
                                        />
                                    </td>
                                </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>;
    };

    return (
        <>
        <Dialog title="Invoice Breakdown" dialogName={props.dialogName} isExitButtonInvisible={false} bodyStyle={{ width: '75vw' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form id={`InvoiceBreakdownForm`} onSubmit={onSubmit}>
                <Page className="dlc-maxer-width">
                        <Page.Section width={14}>
                            <Panel backgroundColor="#D3D3D3">
                                <Panel panelTitle={displayTitle()}>
                                    <div style={{ border: 'solid rgb(99, 147, 193) 1px', padding: '.5rem' }}>
                                        <div className="form-row" style={{display: 'flex', justifyContent: 'center', marginBottom: '0' }}>
                                            <RHFSelect
                                                label={'Pay Periods: '}
                                                name={'payPeriods'}
                                                options={[
                                                    {value: 1, label: '2025-20'},
                                                    {value: 1, label: '2025-19'},
                                                    {value: 1, label: '2025-18'}
                                                ]}
                                                onChange={(e) => setSelectedPayPeriod(e.currentTarget.value)}
                                            />                                            
                                        </div>
                                        <div style={{ border: 'solid rgb(99, 147, 193) 1px', paddingBottom: '.5rem' }}>
                                            {displayOfficeInvoiceSummary()}
                                        </div>
                                    </div>
                                </Panel>
                            </Panel>
                        </Page.Section>
                    </Page>
                    <Page className="dlc-maxer-width">
                        <Page.Section width={14}>
                            <Panel backgroundColor="#D3D3D3">
                                <Panel panelTitle={displayHeading2()}>
                                    <div style={{ border: 'solid rgb(99, 147, 193) 1px', paddingBottom: '.5rem' }}>
                                        {displayInvoicesByOffice()}
                                    </div>
                                </Panel>
                            </Panel>
                        </Page.Section>
                    </Page>
                    <div style={{whiteSpace: 'nowrap', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                        <Button
                            className="blue-hover-button"
                            style={{ width: '24%', marginRight: '1rem' }}
                            text={'Close'}
                            onClick={() => onCancel()}
                        />
                    </div>
                </form>
            </div>
        </Dialog>
        </>
    );
};

export default InvoiceBreakdownDialog;



// WEBPACK FOOTER //
// ./src/components/project-billing/dialogs/InvoiceBreakdownDialog.tsx